import type { MouseEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';

import type { SettingsTabKey } from '@/app/router/paths';
import { PERMISSIONS } from '@/entities/user';
import { IntegrationsPanel } from '@/features/integrations';
import { HeaderTabs, Screen, ScreenEyebrow, useWorkspaceAccess } from '@/shared/ui';

import { DomainsCard } from './components/DomainsCard';
import { TeamCard } from './components/TeamCard';
import { settingsCopy, settingsTabsCopy } from './copy';
import { useTeamMembers } from './hooks';
import {
    settingsTabAt,
    settingsTabForHash,
    settingsTabsFor,
    type SettingsTab,
} from './settings-tabs';
import { BrandingTab } from './tabs/BrandingTab';
import { CompanyTab } from './tabs/CompanyTab';
import { MessagesTab } from './tabs/MessagesTab';
import { ReportTab } from './tabs/ReportTab';
import { LoadingCard, TabColumn } from './tabs/TabLayout';

const TAB_TITLE_ID = 'settings-tab-title';

/**
 * Scherm 11 - Instellingen.
 *
 * Eén donkere kop met de naam van het bedrijf, het aantal gebruikers en de
 * tabbladen: Bedrijf, Huisstijl, Rapport, Berichten, Team en Domeinen
 * (`settings-tabs.ts`). Het zijn dezelfde tabbladen als in de kop van een
 * keuring: links, elk met een eigen adres (`/instellingen/huisstijl`), zodat
 * Terug in de browser en een link naar één tabblad gewoon werken. Onder de kop
 * staat alleen het open tabblad: een korte kop met één zin over wat je daar
 * regelt, en de kaarten.
 *
 * Het eerste tabblad staat op `/instellingen` zelf, waar de rail heen gaat. Een
 * onbekend pad en een tabblad dat je niet mag zien gaan daarheen, zonder een stap
 * in de geschiedenis achter te laten; een adres met hoofdletters of een slash aan
 * het eind wordt het adres van zijn tabblad.
 *
 * Alles komt van de API en wordt daar opgeslagen. Aanpassen doet de eigenaar
 * (`settings.manage`); zonder dat recht staan dezelfde instellingen er, maar zijn
 * ze niet te wijzigen. De kop heeft bewust géén actieknoppen - elke instelling
 * slaat zichzelf op.
 */
export function SettingsPage() {
    const access = useWorkspaceAccess();
    const canManage = access.can(PERMISSIONS.settingsManage);
    const { pathname, hash } = useLocation();
    const navigate = useNavigate();
    const members = useTeamMembers(access.can(PERMISSIONS.usersView));

    /* Kijkt een beheerder mee, dan zijn de rechten van de ander nog onderweg: dan
       nog geen tabbladen, en ook nog nergens heen sturen. */
    const tabs = access.loading ? null : settingsTabsFor(access.can);
    const current = tabs ? settingsTabAt(tabs, pathname) : null;
    const anchored = tabs ? settingsTabForHash(tabs, hash) : null;

    if (tabs && anchored && anchored.key !== current?.key) {
        return <Navigate to={{ pathname: anchored.to, hash }} replace />;
    }

    const first = tabs?.[0];

    if (first && !current) {
        return <Navigate to={first.to} replace />;
    }

    if (current && pathname !== current.to) {
        return <Navigate to={{ pathname: current.to, hash }} replace />;
    }

    const activeMembers = members.data?.filter((member) => member.status === 'active').length;
    const eyebrow = activeMembers === undefined ? '' : settingsCopy.eyebrowUsers(activeMembers);

    /**
     * Een link naar een plek op een ander tabblad - "Ingeschreven als" onder
     * Bedrijfsgegevens staat op Rapport - opent dat tabblad op die plek. Een plek
     * op dit tabblad regelt de kaart zelf (die houdt dan de klik tegen).
     */
    function openOnItsTab(event: MouseEvent<HTMLElement>) {
        if (
            !tabs ||
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
        ) {
            return;
        }

        const href =
            event.target instanceof Element
                ? event.target.closest('a[href^="#"]')?.getAttribute('href')
                : null;
        const tab = href ? settingsTabForHash(tabs, href) : null;

        if (!href || !tab || tab.key === current?.key) {
            return;
        }

        event.preventDefault();
        navigate({ pathname: tab.to, hash: href });
    }

    return (
        <Screen>
            <header className="shrink-0 bg-[var(--ink-800)] px-7 pt-5">
                <ScreenEyebrow text={eyebrow} className="mb-[6px]" />
                <h1 className="m-0 font-[family-name:var(--font-display)] text-[24px] leading-[1.15] font-semibold tracking-[-0.02em] text-[var(--text-on-dark)]">
                    {settingsCopy.title}
                </h1>
                {tabs ? (
                    <HeaderTabs label={settingsCopy.tabsLabel} tabs={tabs} className="mt-3" />
                ) : (
                    /* Zo hoog als de tabbladen, zodat de kop niet verspringt als ze komen. */
                    <div aria-hidden="true" className="mt-3 h-[var(--control-h-md)]" />
                )}
            </header>

            {/* Een eigen werkvlak per tabblad: een ander tabblad begint bovenaan. */}
            <div
                key={current?.key ?? 'laden'}
                className="min-h-0 flex-1 overflow-auto bg-[var(--surface-page)] px-7 pt-[22px] pb-8"
                onClick={openOnItsTab}
            >
                {current ? (
                    <section aria-labelledby={TAB_TITLE_ID} className="@container">
                        <TabIntro tab={current} canManage={canManage} />
                        <TabContent tab={current.key} canManage={canManage} />
                    </section>
                ) : (
                    <div className="max-w-[1120px]">
                        <LoadingCard label={settingsCopy.loading} />
                    </div>
                )}
            </div>
        </Screen>
    );
}

/**
 * De kop van het open tabblad, met één zin over wat je daar regelt. Die zin noemt
 * alleen wat er staat: het tegoed alleen voor wie het ziet.
 */
function TabIntro({ tab, canManage }: { tab: SettingsTab; canManage: boolean }) {
    const intro =
        tab.key === 'company' && canManage
            ? settingsTabsCopy.company.introWithCredits
            : settingsTabsCopy[tab.key].intro;

    return (
        <div className="mb-[18px] max-w-[640px]">
            <h2
                id={TAB_TITLE_ID}
                className="m-0 font-[family-name:var(--font-display)] text-[17px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {tab.label}
            </h2>
            <p className="m-0 mt-1 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                {intro}
            </p>
        </div>
    );
}

/** De kaarten van het open tabblad. Alleen dat tabblad vraagt zijn gegevens op. */
function TabContent({ tab, canManage }: { tab: SettingsTabKey; canManage: boolean }) {
    switch (tab) {
        case 'company':
            return <CompanyTab canManage={canManage} />;
        case 'branding':
            return <BrandingTab canManage={canManage} />;
        case 'report':
            return <ReportTab canManage={canManage} />;
        case 'messages':
            return <MessagesTab canManage={canManage} />;
        case 'team':
            return (
                <TabColumn>
                    <TeamCard />
                </TabColumn>
            );
        case 'domains':
            return (
                <TabColumn>
                    <DomainsCard canManage={canManage} />
                </TabColumn>
            );
        case 'integrations':
            return <IntegrationsPanel owner="company" />;
    }
}
